/**
 * Deepgram Flux Service (Client-Side)
 * 
 * Voice agent service using Deepgram Flux for real-time STT with
 * intelligent end-of-turn detection.
 * 
 * CRITICAL Flux Requirements:
 * - Endpoint: MUST use /v2/listen (NOT /v1/listen - will not work)
 * - Model: MUST specify model=flux-general-en
 * - Chunk Size: 80ms audio chunks strongly recommended (2560 bytes at 16kHz)
 * - Audio Format: linear16 PCM required
 * 
 * SDK Usage: client.listen.v2.connect() for v2 endpoint
 * 
 * End-of-Turn Detection Parameters:
 * - eot_threshold: 0.5-0.9, default 0.7
 * - eager_eot_threshold: 0.3-0.9, default None (must set to enable EagerEndOfTurn)
 * - eot_timeout_ms: 500-10000, default 5000
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

// Flux event types
export const FLUX_EVENTS = {
  OPEN: 'Open',
  TRANSCRIPT: 'Transcript',
  END_OF_TURN: 'EndOfTurn',
  EAGER_END_OF_TURN: 'EagerEndOfTurn',
  TURN_RESUMED: 'TurnResumed',
  METADATA: 'Metadata',
  ERROR: 'Error',
  CLOSE: 'Close',
};

// Connection states
export const CONNECTION_STATE = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error',
};

// WebSocket ready states
const WS_READY_STATE = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
};

// Recommended chunk size for Flux (80ms at 16kHz linear16)
const RECOMMENDED_CHUNK_SIZE = 2560; // bytes

/**
 * Deepgram Flux Service Class
 * 
 * Manages WebSocket connection to Deepgram Flux for voice agent
 * with end-of-turn detection.
 */
class DeepgramFluxService {
  constructor() {
    this.websocket = null;
    this.connectionState = CONNECTION_STATE.DISCONNECTED;
    this.keepAliveInterval = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    /** Set when onerror fires; skip auto-reconnect in onclose to avoid repeated failed attempts */
    this._connectionErrorOccurred = false;

    // Event handlers
    this.eventHandlers = new Map();
    
    // Configuration with Flux defaults
    this.config = {
      // Audio settings
      encoding: 'linear16',
      sampleRate: 16000,
      channels: 1,
      
      // End-of-turn detection
      eotThreshold: 0.7,          // 0.5-0.9, default 0.7
      eagerEotThreshold: 0.5,     // 0.3-0.9, MUST SET to enable EagerEndOfTurn
      eotTimeoutMs: 5000,         // 500-10000, default 5000
    };
    
    // Transcript accumulator
    this.currentTranscript = '';
    this.turnTranscripts = [];
    
    // Firebase functions
    this.functions = getFunctions();
    this.deepgramTokenFn = httpsCallable(this.functions, 'deepgramToken');
  }

  /**
   * Register an event handler
   * @param {string} event - Event name from FLUX_EVENTS
   * @param {Function} handler - Event handler function
   */
  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event).push(handler);
  }

  /**
   * Remove an event handler
   * @param {string} event - Event name
   * @param {Function} handler - Handler to remove
   */
  off(event, handler) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Emit an event to all registered handlers
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  _emit(event, data) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`[DeepgramFlux] Error in ${event} handler:`, error);
        }
      });
    }
  }

  /**
   * Configure the service
   * @param {Object} config - Configuration options
   */
  configure(config = {}) {
    this.config = {
      ...this.config,
      ...config,
    };
    
    // Validate Flux-specific parameters
    if (config.eotThreshold !== undefined) {
      this.config.eotThreshold = Math.max(0.5, Math.min(0.9, config.eotThreshold));
    }
    if (config.eagerEotThreshold !== undefined) {
      this.config.eagerEotThreshold = Math.max(0.3, Math.min(0.9, config.eagerEotThreshold));
    }
    if (config.eotTimeoutMs !== undefined) {
      this.config.eotTimeoutMs = Math.max(500, Math.min(10000, config.eotTimeoutMs));
    }
    
  }

  /**
   * Connect to Deepgram Flux
   * @returns {Promise<boolean>} Success status
   */
  async connect() {
    if (this.connectionState === CONNECTION_STATE.CONNECTED ||
        this.connectionState === CONNECTION_STATE.CONNECTING) {
      return true;
    }

    this._setConnectionState(CONNECTION_STATE.CONNECTING);

    try {

      // Get WebSocket token with Flux configuration
      let tokenResult;
      try {
        tokenResult = await this.deepgramTokenFn({
          mode: 'flux', // CRITICAL: Use 'flux' mode for /v2/listen endpoint
          language: 'en-US', // Flux only supports English
          options: {
            encoding: this.config.encoding,
            sample_rate: this.config.sampleRate,
            eot_threshold: this.config.eotThreshold,
            eager_eot_threshold: this.config.eagerEotThreshold,
            eot_timeout_ms: this.config.eotTimeoutMs,
          },
        });
      } catch (tokenError) {
        // Surface backend errors (e.g. "API key not configured", auth, etc.)
        const code = tokenError?.code || tokenError?.details?.code;
        const msg = tokenError?.message || tokenError?.details?.message || String(tokenError);
        console.error('[DeepgramFlux] Token request failed:', code || msg, msg);
        throw new Error(tokenError?.message || `Deepgram token failed: ${msg}`);
      }

      const { apiKey, wsUrl } = tokenResult.data;

      if (!apiKey || !wsUrl) {
        throw new Error('Failed to get Deepgram Flux credentials');
      }

      // Verify we got the v2 endpoint
      if (!wsUrl.includes('/v2/listen')) {
        console.error('[DeepgramFlux] CRITICAL: Got non-v2 endpoint!', wsUrl);
        throw new Error('Flux requires /v2/listen endpoint');
      }


      // Create WebSocket connection with Deepgram token protocol
      this.websocket = new WebSocket(wsUrl, ['token', apiKey]);

      // Set up event handlers
      this.websocket.onopen = this._handleOpen.bind(this);
      this.websocket.onmessage = this._handleMessage.bind(this);
      this.websocket.onerror = this._handleError.bind(this);
      this.websocket.onclose = this._handleClose.bind(this);

      // Wait for connection
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Flux connection timeout'));
        }, 10000);

        const checkOpen = () => {
          if (this.connectionState === CONNECTION_STATE.CONNECTED) {
            clearTimeout(timeout);
            resolve();
          } else if (this.connectionState === CONNECTION_STATE.ERROR) {
            clearTimeout(timeout);
            reject(new Error('Flux connection failed'));
          } else {
            setTimeout(checkOpen, 100);
          }
        };
        checkOpen();
      });

      return true;

    } catch (error) {
      console.error('[DeepgramFlux] Connection error:', error?.message || error);
      if (this._lastCloseCode !== undefined) {
        console.error('[DeepgramFlux] WebSocket close code:', this._lastCloseCode, 'reason:', this._lastCloseReason || 'none');
      }
      this._setConnectionState(CONNECTION_STATE.ERROR);
      this._emit(FLUX_EVENTS.ERROR, { error });
      // One-line troubleshooting hint for common causes
      const hint = error?.message === 'Flux connection timeout'
        ? 'Check: 1) Firebase function deepgramToken is deployed and Deepgram API key is set (firebase functions:config:set deepgram.api_key="..."). 2) Network/firewall allows WebSocket to Deepgram.'
        : error?.message === 'Flux connection failed'
          ? 'WebSocket opened then failed. Check server logs; Flux URL params may be invalid (v2/listen only).'
          : null;
      if (hint) console.warn('[DeepgramFlux]', hint);
      return false;
    }
  }

  /**
   * Set connection state and notify listeners
   * @param {string} state - New connection state
   */
  _setConnectionState(state) {
    this.connectionState = state;
    this._emit('connectionStateChange', state);
  }

  /**
   * Handle WebSocket open
   */
  _handleOpen() {
    this._setConnectionState(CONNECTION_STATE.CONNECTED);
    this.reconnectAttempts = 0;
    this.currentTranscript = '';
    this.turnTranscripts = [];
    this._emit(FLUX_EVENTS.OPEN, {});
    
    // Note: Flux handles keepalive internally, but we still send periodic signals
    // to be safe during long pauses
    this._startKeepAlive();
  }

  /**
   * Handle incoming Flux messages
   * @param {MessageEvent} event - WebSocket message
   */
  _handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      const msgType = data.type;

      switch (msgType) {
        case 'Results':
          this._handleTranscript(data);
          break;

        case 'EndOfTurn':
          this._handleEndOfTurn(data);
          break;

        case 'EagerEndOfTurn':
          this._handleEagerEndOfTurn(data);
          break;

        case 'TurnResumed':
          this._handleTurnResumed(data);
          break;

        case 'Metadata':
          this._emit(FLUX_EVENTS.METADATA, data);
          break;

        case 'Error':
          console.error('[DeepgramFlux] Server error:', data);
          this._emit(FLUX_EVENTS.ERROR, { error: new Error(data.message || 'Flux error') });
          break;

        default:
      }

    } catch (error) {
      console.error('[DeepgramFlux] Error parsing message:', error);
    }
  }

  /**
   * Handle transcript results
   * @param {Object} data - Transcript data
   */
  _handleTranscript(data) {
    const alternative = data.channel?.alternatives?.[0];
    if (!alternative) return;

    const transcript = alternative.transcript || '';
    const words = alternative.words || [];
    const confidence = alternative.confidence || 0;

    // Update current transcript
    if (transcript) {
      this.currentTranscript = transcript;
    }

    this._emit(FLUX_EVENTS.TRANSCRIPT, {
      transcript,
      words,
      confidence,
      isFinal: data.is_final,
      speechFinal: data.speech_final,
    });
  }

  /**
   * Handle EndOfTurn event
   * Indicates the speaker has finished their turn with high confidence.
   * @param {Object} data - EndOfTurn data
   */
  _handleEndOfTurn(data) {
    
    // Store the completed turn
    if (this.currentTranscript) {
      this.turnTranscripts.push({
        text: this.currentTranscript,
        timestamp: Date.now(),
      });
    }

    this._emit(FLUX_EVENTS.END_OF_TURN, {
      transcript: this.currentTranscript,
      turnTranscripts: [...this.turnTranscripts],
      confidence: data.confidence,
    });

    // Reset for next turn
    this.currentTranscript = '';
  }

  /**
   * Handle EagerEndOfTurn event
   * Early indication that speaker might be done. Use to start LLM processing.
   * May be followed by TurnResumed if speaker continues.
   * @param {Object} data - EagerEndOfTurn data
   */
  _handleEagerEndOfTurn(data) {
    
    this._emit(FLUX_EVENTS.EAGER_END_OF_TURN, {
      transcript: this.currentTranscript,
      confidence: data.confidence,
      // Signal that LLM processing can begin speculatively
      canStartLLM: true,
    });
  }

  /**
   * Handle TurnResumed event
   * Speaker continued speaking after EagerEndOfTurn. Cancel speculative LLM calls.
   * @param {Object} data - TurnResumed data
   */
  _handleTurnResumed(data) {
    
    this._emit(FLUX_EVENTS.TURN_RESUMED, {
      transcript: this.currentTranscript,
      // Signal to cancel any speculative LLM processing
      shouldCancelSpeculative: true,
    });
  }

  /**
   * Handle WebSocket error
   * @param {Event} event - Error event
   */
  _handleError(event) {
    console.error('[DeepgramFlux] WebSocket error:', event);
    this._connectionErrorOccurred = true;
    this._setConnectionState(CONNECTION_STATE.ERROR);
    this._emit(FLUX_EVENTS.ERROR, { error: new Error('WebSocket error') });
  }

  /**
   * Handle WebSocket close
   * @param {CloseEvent} event - Close event
   */
  _handleClose(event) {
    this._lastCloseCode = event.code;
    this._lastCloseReason = event.reason;
    this._stopKeepAlive();
    this._setConnectionState(CONNECTION_STATE.DISCONNECTED);

    // Do not auto-reconnect if the close was due to a WebSocket error (e.g. auth/config failure)
    const wasError = this._connectionErrorOccurred;
    this._connectionErrorOccurred = false;
    this._emit(FLUX_EVENTS.CLOSE, { code: event.code, reason: event.reason });

    if (wasError) {
      console.warn('[DeepgramFlux] Skipping reconnect after WebSocket error. Close code:', event.code, 'reason:', event.reason || 'none');
      return;
    }

    // Attempt reconnection only for unexpected clean closes (e.g. network drop)
    if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      setTimeout(() => this.connect(), 1000 * this.reconnectAttempts);
    }
  }

  /**
   * Start keep-alive interval
   */
  _startKeepAlive() {
    this._stopKeepAlive();
    this.keepAliveInterval = setInterval(() => {
      this.sendKeepAlive();
    }, 10000);
  }

  /**
   * Stop keep-alive interval
   */
  _stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  /**
   * Send keep-alive message
   */
  sendKeepAlive() {
    if (this.isConnected()) {
      try {
        this.websocket.send(JSON.stringify({ type: 'KeepAlive' }));
      } catch (e) {
        console.warn('[DeepgramFlux] KeepAlive failed:', e.message);
      }
    }
  }

  /**
   * Send audio data to Flux
   * IMPORTANT: Send 80ms chunks (2560 bytes at 16kHz linear16) for optimal performance
   * 
   * @param {ArrayBuffer|Uint8Array} audioData - Raw audio data (linear16 PCM)
   * @returns {boolean} Success status
   */
  sendAudio(audioData) {
    if (!this.isConnected()) {
      console.warn('[DeepgramFlux] Cannot send audio - not connected');
      return false;
    }

    try {
      // Log if chunk size is not optimal
      if (audioData.byteLength !== RECOMMENDED_CHUNK_SIZE) {
        // Only log occasionally to avoid spam
        if (Math.random() < 0.01) {
        }
      }

      this.websocket.send(audioData);
      return true;
    } catch (error) {
      console.error('[DeepgramFlux] Error sending audio:', error);
      this._emit(FLUX_EVENTS.ERROR, { error });
      return false;
    }
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  isConnected() {
    return this.connectionState === CONNECTION_STATE.CONNECTED &&
           this.websocket?.readyState === WS_READY_STATE.OPEN;
  }

  /**
   * Disconnect from Flux
   */
  disconnect() {
    this._stopKeepAlive();

    if (this.websocket) {
      if (this.websocket.readyState === WS_READY_STATE.OPEN) {
        try {
          this.websocket.send(JSON.stringify({ type: 'CloseStream' }));
        } catch (e) {
          // Ignore
        }
      }
      this.websocket.close(1000, 'Client disconnect');
      this.websocket = null;
    }

    this._setConnectionState(CONNECTION_STATE.DISCONNECTED);
    this.currentTranscript = '';
    this.turnTranscripts = [];
  }

  /**
   * Get accumulated transcripts from all turns
   * @returns {Array} Turn transcripts
   */
  getTurnTranscripts() {
    return [...this.turnTranscripts];
  }

  /**
   * Clear turn transcripts
   */
  clearTranscripts() {
    this.turnTranscripts = [];
    this.currentTranscript = '';
  }

  /**
   * Get service status
   * @returns {Object} Status information
   */
  getStatus() {
    return {
      connectionState: this.connectionState,
      isConnected: this.isConnected(),
      config: this.config,
      turnCount: this.turnTranscripts.length,
      currentTranscript: this.currentTranscript,
    };
  }
}

// Export singleton instance
export const deepgramFluxService = new DeepgramFluxService();

// Export class for testing
export { DeepgramFluxService };

// Export recommended chunk size
export { RECOMMENDED_CHUNK_SIZE };

export default deepgramFluxService;
